import React, { useRef } from "react";

import BuildIcon from '@mui/icons-material/Build';
import CompressIcon from '@mui/icons-material/Compress';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";

export interface ToolBarProps {
  onMinifyClick: () => void;
  onPrettifyClick: () => void;
  onClearClick: () => void;
  onAutoPrettifyChange: () => void;
  onDownloadClick: () => void;
  onUploadClick: (fileContent: File) => void;
  onFixClick: () => void;
  onShowSchemaChange: () => void;
  isAutoPrettifyOn: boolean;
  isValidJson: boolean;
}

interface FileUploaderProps {
  onFileHandle: (fileContent: File) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onFileHandle }) => {
  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
      inputFileRef.current.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const fileUploaded = e.target.files[0];
    onFileHandle(fileUploaded);
  };

  return (
    <>
      <Button
        variant="text"
        onClick={handleUploadClick}
        startIcon={<UploadIcon />}
        size="small"
      >
        Upload
      </Button>
      <input
        ref={inputFileRef}
        style={{ display: "none" }}
        onChange={handleChange}
        type="file"
        accept="application/json"
      />
    </>
  );
};

export const ToolBar: React.FC<ToolBarProps> = ({
  onMinifyClick,
  onPrettifyClick,
  isAutoPrettifyOn,
  onAutoPrettifyChange,
  onClearClick,
  onDownloadClick,
  onUploadClick,
  onFixClick,
  isValidJson,
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '40px' }}>
      <FileUploader onFileHandle={onUploadClick} />
      <Button
        variant="text"
        disabled={!isValidJson}
        onClick={onDownloadClick}
        startIcon={<DownloadIcon />}
        size="small"
      >
        Download
      </Button>
      <Button
        variant="text"
        disabled={!isValidJson}
        onClick={onClearClick}
        startIcon={<DeleteIcon />}
        size="small"
      >
        Clear
      </Button>
      <Button
        variant="text"
        disabled={isValidJson}
        onClick={onFixClick}
        startIcon={<BuildIcon />}
        size="small"
      >
        Fix
      </Button>
      <Button
        variant="text"
        disabled={!isValidJson || isAutoPrettifyOn}
        onClick={onMinifyClick}
        startIcon={<CompressIcon />}
        size="small"
      >
        Minify
      </Button>
      <Button
        variant="text"
        disabled={!isValidJson || isAutoPrettifyOn}
        onClick={onPrettifyClick}
        startIcon={<DataObjectIcon />}
        size="small"
      >
        Prettify
      </Button>
      <FormControlLabel
        control={
          <Checkbox
            checked={isAutoPrettifyOn}
            onChange={onAutoPrettifyChange}
            size="small"
          />
        }
        label={
          <Typography variant="body2">Auto Prettify</Typography> // Adjust the variant to control size
        }
      />
    </Box>
  );
};
